import {
  AssociationEntity,
  AssociationResponse,
  ExternalSystem,
  IntegrationLogResponse,
  IntegrationLogStatusEnum,
  toExternalSystem,
} from '@monetize/types/app';
import { orderObjectsBy } from '@monetize/utils/core';
import { useMemo } from 'react';
import { useGetEntitySyncInfo } from '../../api/associationsService';
import {
  useHasAccountingConfigured,
  useHasCrmConfigured,
} from '../../api/settingsService';
import { TargetSystemTypeEnumDisplay } from '../IntegrationLog/constants';

/** Cadence while the popover is open — slow enough to be background noise. */
const SYNC_INFO_POLL_MS = 15_000;

/** An external record this MonetizeNow record is linked to. */
export interface ExternalReference {
  id: string;
  /** Only association records carry a deep link; detected ids do not. */
  url?: string;
}

/**
 * One external system's standing on a record: what it points at, and how its
 * last sync went. Both the icon row and the popover render from this.
 */
export interface IntegrationSystemSummary {
  system: ExternalSystem;
  label: string;
  references: ExternalReference[];
  /**
   * The connector keeps a single row per record per system, updating it in
   * place, so this is the whole sync story rather than the head of a feed.
   */
  latestLog?: IntegrationLogResponse;
  /** The most recent sync for this system failed. */
  hasFailure: boolean;
  /**
   * Nothing links this record to the system: no association, no id on a sync
   * log, and no sync at all. Anything less is unknown rather than false, so it
   * goes unreported — never assert a disconnection we cannot see.
   */
  isNotConnected: boolean;
}

interface UseIntegrationInfoOptions {
  entity: AssociationEntity;
  recordId: string;
  showWarningIfNotAssociated?: boolean;
  /** Keep polling; set while the popover is open so a sync lands on screen. */
  pollForUpdates?: boolean;
}

interface UseIntegrationInfoResult {
  systems: IntegrationSystemSummary[];
  isLoading: boolean;
  /** True while a refetch is in flight, including the initial load. */
  isFetching: boolean;
  refetch: () => void;
}

const EMPTY_SYNC_INFO: {
  associations: AssociationResponse[];
  syncLogs: IntegrationLogResponse[];
} = { associations: [], syncLogs: [] };

const getSystemLabel = (system: ExternalSystem) =>
  TargetSystemTypeEnumDisplay[system] ?? system;

export function useIntegrationInfo({
  entity,
  recordId,
  showWarningIfNotAssociated = false,
  pollForUpdates = false,
}: UseIntegrationInfoOptions): UseIntegrationInfoResult {
  const { data: crmConfigData, isLoading: isLoadingCrm } = useHasCrmConfigured({
    gcTime: Infinity,
    staleTime: Infinity,
    refetchOnMount: false,
  });

  const { data: accountingConfigData, isLoading: isLoadingAccounting } =
    useHasAccountingConfigured({
      gcTime: Infinity,
      staleTime: Infinity,
      refetchOnMount: false,
    });

  const {
    data: syncInfo = EMPTY_SYNC_INFO,
    isLoading: isLoadingSyncInfo,
    isFetching,
    refetch,
  } = useGetEntitySyncInfo(
    { entity, recordId },
    {
      retry: false,
      refetchInterval: pollForUpdates ? SYNC_INFO_POLL_MS : false,
      enabled:
        !!recordId &&
        !!(
          crmConfigData?.crmConnected ||
          accountingConfigData?.accountingConnected
        ),
    },
  );

  const { associations, syncLogs } = syncInfo;

  const systems = useMemo(() => {
    const associationsBySystem = new Map<ExternalSystem, AssociationResponse[]>(
      [],
    );
    for (const association of associations) {
      const existing =
        associationsBySystem.get(association.externalSystem) ?? [];
      existing.push(association);
      associationsBySystem.set(association.externalSystem, existing);
    }

    // Sync logs name their target system capitalised, and a connector can name
    // one this enum does not carry yet. Those get no row — there is no icon or
    // label to render them with — but `View all in Integration Logs` still has
    // them.
    const logsBySystem = new Map<ExternalSystem, IntegrationLogResponse[]>([]);
    for (const log of syncLogs) {
      const system = toExternalSystem(log.targetSystemType);
      if (!system) {
        continue;
      }
      const existing = logsBySystem.get(system) ?? [];
      existing.push(log);
      logsBySystem.set(system, existing);
    }

    const allSystems = new Set<ExternalSystem>([
      ...associationsBySystem.keys(),
      ...logsBySystem.keys(),
    ]);

    // Surface the configured CRM even when it has no association and no sync
    // activity at all — that silence is exactly what the warning icon reports.
    if (
      showWarningIfNotAssociated &&
      crmConfigData?.crmConnected &&
      crmConfigData.system &&
      // HubSpot does not have associations
      crmConfigData.system !== ExternalSystem.HUBSPOT
    ) {
      allSystems.add(crmConfigData.system);
    }

    const summaries: IntegrationSystemSummary[] = [];
    for (const system of allSystems) {
      const systemAssociations = associationsBySystem.get(system) ?? [];
      const [latestLog] = logsBySystem.get(system) ?? [];

      const references: ExternalReference[] = systemAssociations.map(
        ({ externalObjectId, externalObjectUrl }) => ({
          id: externalObjectId,
          url: externalObjectUrl,
        }),
      );
      // Associations win — they are the only ones carrying a deep link — but
      // plenty of records have none: quotes never persist one, HubSpot has no
      // associations at all. The sync log's targetId is then the only proof of
      // what MonetizeNow wrote to.
      if (references.length === 0 && latestLog?.targetId) {
        references.push({ id: latestLog.targetId });
      }

      summaries.push({
        system,
        label: getSystemLabel(system),
        references,
        latestLog,
        hasFailure: latestLog?.status === IntegrationLogStatusEnum.FAILED,
        // A sync that ran at all means the two systems are talking, so an id we
        // could not read off it is unknown, not absent. Only total silence —
        // no id, no sync — is reported as not connected.
        isNotConnected: references.length === 0 && !latestLog,
      });
    }

    return orderObjectsBy(summaries, ['system']);
  }, [associations, syncLogs, crmConfigData, showWarningIfNotAssociated]);

  return {
    systems,
    isLoading: isLoadingCrm || isLoadingAccounting || isLoadingSyncInfo,
    isFetching,
    refetch,
  };
}
